import { useEffect, useId, useState } from "react";
// import Dropdown from "../Dropdown";
import { Dropdown } from "@iq/models";
import styles from "./Input.module.css";
import { FiPlus } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { FaDollarSign } from "react-icons/fa6";

const TEXTAREA_TYPE = "textarea";
const DROPDOWN_TYPE = "dropdown";
const ADDITEM_TYPE = "addItem";
const MONEY_TYPE = "money";

/**
 * Parses a CSS-like declaration string (e.g. "gridColumn: 2 / span 2;
 * gridRow: 1") into a React inline-style object. Declarations are separated
 * by `;`, keys may be camelCase (used as-is) or kebab-case (converted to
 * camelCase). Object input is returned unchanged; falsy input returns
 * undefined.
 */
export function parseInlineStyle(style) {
	if (!style) {
		return undefined;
	}

	if (typeof style !== "string") {
		return style;
	}

	const result = {};

	style.split(";").forEach((declaration) => {
		const trimmed = declaration.trim();
		if (!trimmed) {
			return;
		}

		const colonIndex = trimmed.indexOf(":");
		if (colonIndex === -1) {
			return;
		}

		const rawKey = trimmed.slice(0, colonIndex).trim();
		const rawValue = trimmed.slice(colonIndex + 1).trim();
		if (!rawKey || !rawValue) {
			return;
		}

		const camelKey = rawKey.includes("-")
			? rawKey.replace(/-([a-z])/g, (_, char) => char.toUpperCase())
			: rawKey;

		result[camelKey] = rawValue;
	});

	return result;
}

/**
 * Universal input model. Renders the appropriate underlying element based on
 * `type` while sharing the same light/dark styling as the Dropdown model
 * (same control height, background, and placeholder treatment).
 *
 * Supported types: text, textarea, number, email, password, date, search,
 * tel, dropdown (delegates to the Dropdown model).
 *
 * `style` accepts either a plain object or a CSS-like declaration string
 * (e.g. `"gridColumn: 2 / span 2"`) and is applied to the field's outer
 * wrapper - handy for grid placement without a dedicated CSS class per field.
 */
export default function Input({
	id,
	name,
	type = "text",
	value = "",
	placeholder = "",
	colorScheme = "light",
	disabled = false,
	readOnly = false,
	required = false,
	rows = 4,
	onChange,
	onFocus,
	onBlur,
	className,
	fieldClassName,
	style,
	ariaLabel,
	autoComplete = "off",
	// Dropdown-specific props (only used when type="dropdown")
	options = [],
	allowCustom = false,
	onOptionSelect,
	renderInPortal = false,
	menuClassName,
	onAddItem,
	onItemsChange,
	numberPicker = false,
	onNumberPickerUpClick,
	onNumberPickerDownClick,
	initialItems,
	// Escape hatch: pass a custom Dropdown render-prop (same signature as the
	// Dropdown model's `children`) for advanced cases (typeahead filtering,
	// custom loading/empty states, etc.) instead of the simple `options` list.
	children,
	label = null,
	...rest
}) {
	const generatedId = useId();
	const inputId = id || generatedId;
	const [addItemValue, setAddItemValue] = useState("");
	const [items, setItems] = useState(() => (Array.isArray(initialItems) ? initialItems : []));

	const themeClass = colorScheme === "dark" ? styles.dark : styles.light;
	const resolvedStyle = parseInlineStyle(style);

	useEffect(() => {
		if (type !== ADDITEM_TYPE) {
			return;
		}

		if (Array.isArray(initialItems)) {
			setItems(initialItems);
		}
	}, [initialItems, type]);

	useEffect(() => {
		if (type !== ADDITEM_TYPE) {
			return;
		}

		onItemsChange?.(items);
	}, [items, onItemsChange, type]);

	if (type === DROPDOWN_TYPE) {
		return (
			<div className={label ? styles.inputWithLabel : styles.inputWithoutLabel}>
				{label && (
					<label htmlFor={inputId} className={styles.label}>
						{label}
						{required && <span className={styles.requiredIndicator}>*</span>}
					</label>
				)}
				<Dropdown
					id={inputId}
					name={name}
					value={value}
					placeholder={placeholder}
					allowCustom={allowCustom}
					colorScheme={colorScheme}
					disabled={disabled}
					onInputChange={onChange}
					className={className}
					style={resolvedStyle}
					inputClassName={[styles.field, fieldClassName].filter(Boolean).join(" ")}
					menuClassName={menuClassName}
					ariaLabel={ariaLabel}
					renderInPortal={renderInPortal}
				>
					{children || (({ closeMenu }) => (
						<>
							{options.map((option) => (
								<button
									type="button"
									key={option.value}
									className="dropdown-menu-item"
									onClick={(event) => {
										event.stopPropagation();
										onOptionSelect?.(option);
										closeMenu();
									}}
								>
									{option.label}
								</button>
							))}
						</>
					))}
				</Dropdown>
			</div>
		);
	}

	const wrapperClassName = [styles.input, themeClass, className].filter(Boolean).join(" ");

	if (type === TEXTAREA_TYPE) {
		return (
			<div className={label ? styles.inputWithLabel : styles.inputWithoutLabel} style={resolvedStyle}>
				{label && (
					<label htmlFor={inputId} className={styles.label}>
						{label}
						{required && <span className={styles.requiredIndicator}>*</span>}
					</label>
				)}
				<div className={wrapperClassName}>
					<textarea
						id={inputId}
						name={name}
						value={value}
						placeholder={placeholder}
						onChange={onChange}
						onFocus={onFocus}
						onBlur={onBlur}
						disabled={disabled}
						readOnly={readOnly}
						rows={rows}
						autoComplete={autoComplete}
						aria-label={ariaLabel}
						className={[styles.field, styles.textareaField, fieldClassName].filter(Boolean).join(" ")}
						{...rest}
					/>
				</div>
			</div>
		);
	}

	if (type === ADDITEM_TYPE) {
		const normalizedValue = addItemValue.trim();

		const parseItems = (rawValue) => rawValue
			.split(",")
			.map((item) => item.trim())
			.filter(Boolean);

		const handleAddItem = (event) => {
			event.preventDefault();

			if (!normalizedValue || disabled || readOnly) {
				return;
			}

			const parsedItems = parseItems(normalizedValue);
			if (parsedItems.length === 0) {
				return;
			}

			setItems((prevItems) => [...prevItems, ...parsedItems]);

			onAddItem?.(parsedItems);
			setAddItemValue("");
		};

		const handleRemoveItem = (indexToRemove) => {
			setItems((prevItems) => prevItems.filter((_, index) => index !== indexToRemove));
		};

		const handleInputChange = (event) => {
			setAddItemValue(event.target.value);
			onChange?.(event);
		};

		const handleKeyDown = (event) => {
			if (event.key === "Enter") {
				handleAddItem(event);
			}

			rest.onKeyDown?.(event);
		};

		return (
			<div className={label ? styles.inputWithLabel : styles.inputWithoutLabel} style={resolvedStyle}>
				{label && (
					<label htmlFor={inputId} className={styles.label}>
						{label}
						{required && <span className={styles.requiredIndicator}>*</span>}
					</label>
				)}
				<div className={styles.addItemInputContainer}>
					<div className={wrapperClassName}>
						<input
							id={inputId}
							name={name}
							type="text"
							value={addItemValue}
							placeholder={placeholder}
							onChange={handleInputChange}
							onFocus={onFocus}
							onBlur={onBlur}
							onKeyDown={handleKeyDown}
							disabled={disabled}
							readOnly={readOnly}
							autoComplete={autoComplete}
							aria-label={ariaLabel}
							className={[styles.field, fieldClassName].filter(Boolean).join(" ")}
							{...rest}
						/>
					</div>
					<button
						type="button"
						className={styles.addItemButton}
						onClick={handleAddItem}
						disabled={disabled || readOnly || !normalizedValue}
						aria-label="Add item"
					>
						<FiPlus size={24} color={"var(--fg-default)"} />
					</button>
				</div>
				{items.length > 0 && (
					<div className={styles.addItemList}>
						{items.map((item, index) => (
							<span key={`${item}-${index}`} className={styles.addItemChip}>
								{item}
								<button
									type="button"
									className={styles.removeItemButton}
									onClick={() => handleRemoveItem(index)}
									disabled={disabled || readOnly}
									aria-label={`Remove ${item}`}
								>
									<IoClose size={16} color={"var(--fg-muted)"} />
								</button>
							</span>
						))}
					</div>
				)}
			</div>
		);
	}

	if (type === MONEY_TYPE) {
		return (
				<div className={label ? styles.inputWithLabel : styles.inputWithoutLabel} style={resolvedStyle}>
					{label && (
						<label htmlFor={inputId} className={styles.label}>
							{label}
						{required && <span className={styles.requiredIndicator}>*</span>}
						</label>
					)}
					<div className={wrapperClassName} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<FaDollarSign size={16} className={styles.moneyIcon} />
						<input
							id={inputId}
							name={name}
							type="number"
							value={value}
							placeholder={placeholder}
							onChange={onChange}
							onFocus={onFocus}
							onBlur={onBlur}
							disabled={disabled}
							readOnly={readOnly}
							autoComplete={autoComplete}
							aria-label={ariaLabel}
							className={[styles.field, fieldClassName].filter(Boolean).join(" ")}
							{...rest}
						/>
						{numberPicker && (
							<div className={styles.numberPickerControlsContainer}>
								<button
									type="button"
									onClick={() => onNumberPickerUpClick?.()}
									className={styles.numberPickerButton}
								>
									<IoChevronUp size={16} className={styles.numberPickerIcon} />
								</button>
								<button
									type="button"
									onClick={() => onNumberPickerDownClick?.()}
									className={styles.numberPickerButton}
								>
									<IoChevronDown size={16} className={styles.numberPickerIcon} />
								</button>
							</div>
						)}
					</div>

					
				</div>
			);
	}

	return (
		<div className={label ? styles.inputWithLabel : styles.inputWithoutLabel} style={resolvedStyle}>
			{label && (
				<label htmlFor={inputId} className={styles.label}>
					{label}
					{required && <span className={styles.requiredIndicator}>*</span>}
				</label>
			)}
			<div className={wrapperClassName}>
				<input
					id={inputId}
					name={name}
					type={type}
					value={value}
					placeholder={placeholder}
					onChange={onChange}
					onFocus={onFocus}
					onBlur={onBlur}
					disabled={disabled}
					readOnly={readOnly}
					autoComplete={autoComplete}
					aria-label={ariaLabel}
					className={[styles.field, fieldClassName].filter(Boolean).join(" ")}
					{...rest}
				/>
				{numberPicker && (
					<div className={styles.numberPickerControlsContainer}>
						<button
							type="button"
							onClick={() => onNumberPickerUpClick?.()}
							className={styles.numberPickerButton}
						>
							<IoChevronUp size={16} className={styles.numberPickerIcon} />
						</button>
						<button
							type="button"
							onClick={() => onNumberPickerDownClick?.()}
							className={styles.numberPickerButton}
						>
							<IoChevronDown size={16} className={styles.numberPickerIcon} />
						</button>
					</div>
				)}
			</div>

			
		</div>
	);
}
